import { useCallback, useEffect, useMemo, useState } from 'react';
import { createInsightsApiClient } from '../../../lib/apiClient';
import type { ThreadSummary } from '../InsightsPage.types';

interface InsightsThreadsResponse {
  threads?: ThreadSummary[];
}

interface UseInsightsThreadHistoryResult {
  threadHistory: ThreadSummary[];
  isLoadingHistory: boolean;
  refreshThreadHistory: () => Promise<void>;
}

export const useInsightsThreadHistory = (): UseInsightsThreadHistoryResult => {
  const insightsClient = useMemo(() => createInsightsApiClient(), []);
  const [threadHistory, setThreadHistory] = useState<ThreadSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);

  const refreshThreadHistory = useCallback(async (): Promise<void> => {
    setIsLoadingHistory(true);

    try {
      const response = await insightsClient
        .get('insights/threads', {
          searchParams: { limit: '10' },
        })
        .json<InsightsThreadsResponse>();

      setThreadHistory(Array.isArray(response.threads) ? response.threads : []);
    } catch {
      // Thread history is a non-critical enhancement for the chat experience.
      setThreadHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [insightsClient]);

  useEffect(() => {
    void refreshThreadHistory();
  }, [refreshThreadHistory]);

  return {
    threadHistory,
    isLoadingHistory,
    refreshThreadHistory,
  };
};
